/**
 * @quotech/ui - Universal Header Component
 *
 * Compact header with:
 * - Mobile menu button
 * - Breadcrumbs / Job Context
 * - Global search
 * - Quick Add
 * - Notifications
 * - Theme toggle
 * - User avatar (compact)
 */

import { useState, useRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Menu, Search, Command, Sun, Moon, X, ChevronRight, Bell } from 'lucide-react'
import { MobileDockTriggerStrip } from './universal-dock/MobileDockTriggerStrip'
import { formatCountBadge } from '../lib/format'
import { cn, formatModFShortcut, isMac } from '../lib/utils'
import { HEADER_MOBILE_NAV_SLOT_ID, HEADER_MOBILE_TITLE_SLOT_ID } from '../mobile/headerTitleSlot'
import { useCloseOnOutsidePointer } from '../hooks/useCloseOnOutsidePointer'
import { HeaderBackButton } from './header/BackButton'
import { OrgSwitcher } from './header/OrgSwitcher'
import { UserMenu } from './header/UserMenu'
import type { BreadcrumbItem, User, HubId, QuickAddItem, JobContext, ModuleConfig, OrganisationOption, ProfileAlias } from './types'

export interface HeaderProps {
  module?: ModuleConfig
  breadcrumbs?: BreadcrumbItem[]
  user?: User | null
  currentHub?: HubId
  pinnedHubs?: HubId[]
  showHubSwitcher?: boolean
  showOrgSwitcher?: boolean
  organisations?: OrganisationOption[]
  currentOrganisationId?: string
  onSelectOrganisation?: (org: OrganisationOption) => void
  onOrgClick?: () => void
  theme?: 'light' | 'dark'
  onToggleTheme?: () => void
  onOpenSearch?: () => void
  onOpenMobileSidebar?: (mode?: 'hover' | 'click') => void
  showNotifications?: boolean
  notificationCount?: number
  onNotificationsClick?: () => void
  quickAddItems?: QuickAddItem[]
  showQuickAdd?: boolean
  jobContext?: JobContext | null
  onClearJobContext?: () => void
  onOpenCustomize?: () => void
  onLogout?: () => void
  onUseProfile?: (alias: ProfileAlias) => void
  /** Opens the settings modal on a tab. Absent hosts keep a sign-out-only menu. */
  onOpenSettings?: (tabId?: string) => void
  className?: string
  headerRightActionsSlot?: ReactNode
  quietMode?: boolean
  showAIButton?: boolean
  onOpenAI?: () => void
  /**
   * Suppress the mobile Report/Teams/AI trigger strip. Set by hosts that
   * suppress the UniversalDock (e.g. the master shell passes its own legacy
   * BottomDock via `hideBottomDock`): the strip dispatches dock-panel events
   * nothing is listening for there, so it would be three inert buttons eating
   * the limited mobile header runway.
   */
  hideMobileDockTriggers?: boolean
  /** Drop both search buttons (field crew: Diary, Emails, Calendar only). */
  hideSearch?: boolean
  /** Theme toggle lives in the avatar menu instead of the bar. */
  themeInUserMenu?: boolean
  /**
   * FEAT-2026-07-03-HEADER-INLINE-TABS — open-page tabs rendered INSIDE the
   * header row, right of the breadcrumbs (pass `<TabBar variant="inline" …/>`).
   * The breadcrumb + tab strip used to be TWO stacked rows; the operator:
   * "they should overlay in the same line ... this will allow free space in
   * that row, make the screen wider and more useable". When set:
   *   - the breadcrumb trail caps at its LAST 3 crumbs (a leading "…" carries
   *     the full trail in its tooltip), and
   *   - the slot takes the row's flexible middle (min-w-0 → the TabBar's own
   *     overflow-x scroll absorbs many tabs).
   * Omit → the header renders exactly as before (hosts still on the stacked
   * `tabBarSlot` row are untouched).
   */
  tabsSlot?: ReactNode
  /**
   * Phone-only: the page puts its own tabs in this header row (portal into
   * HEADER_MOBILE_NAV_SLOT_ID). On a phone the row then keeps only those tabs,
   * the bell and the avatar; search and theme move into the avatar menu, and
   * the company switcher stays only for people in more than one company.
   * Off by default, so every other hub renders exactly as before. Diary only.
   */
  mobileNavHost?: boolean
}

/** Max crumbs shown when tabs share the header line (last N, "…"-prefixed). */
const INLINE_TABS_CRUMB_CAP = 3

export function Header({
  module, breadcrumbs, user, theme = 'light',
  onToggleTheme, onOpenSearch, onOpenMobileSidebar,
  showOrgSwitcher, organisations = [], currentOrganisationId,
  onSelectOrganisation, onOrgClick,
  showNotifications = false, notificationCount = 0,
  onNotificationsClick, quickAddItems = [], showQuickAdd = true,
  jobContext, onClearJobContext, onLogout, onUseProfile, onOpenSettings, className,
  headerRightActionsSlot, quietMode = false,
  hideMobileDockTriggers = false,
  hideSearch = false, themeInUserMenu = false,
  tabsSlot,
  mobileNavHost = false,
}: HeaderProps) {
  const showSearch = !!onOpenSearch && !quietMode && !hideSearch
  const accentBgClass = module?.accentBgClass || 'bg-teal-600'
  // With the page's tabs in the row, a phone shows only tabs, bell and avatar.
  const phoneHidden = mobileNavHost ? 'max-sm:hidden' : ''
  const keepOrgOnPhone = organisations.length > 1
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const quickAddRef = useRef<HTMLDivElement>(null)
  const lastMenuOpenRef = useRef<number>(0)

  // FEAT-2026-07-03-HEADER-INLINE-TABS — with tabs sharing the line, cap the
  // trail at the last 3 crumbs so deep paths don't squeeze the tabs out. The
  // "…" prefix keeps the full trail discoverable (tooltip).
  const crumbsCapped = !!tabsSlot && !!breadcrumbs && breadcrumbs.length > INLINE_TABS_CRUMB_CAP
  const visibleBreadcrumbs = crumbsCapped
    ? breadcrumbs!.slice(-INLINE_TABS_CRUMB_CAP)
    : breadcrumbs

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(quickAddOpen, () => setQuickAddOpen(false), [quickAddRef])

  return (
    <header
      className={cn('bg-card border-b border-border', className)}
      // Mobile safe-area (2026-07-01, operator "The top in the mobile app view
      // gets cut off from camera.. do a better design"). Once the shell paints
      // edge-to-edge (viewport-fit=cover — already set in the master index.html)
      // the control row rendered UNDER the notch / front camera. Reserve the
      // physical top inset as padding and paint bg-card up into it; the controls
      // stay in the inner FIXED-HEIGHT row below because the iOS top inset
      // (~47-59px) can EXCEED the 48px bar — a border-box padding on a
      // fixed-height <header> would crush the row. env() is 0 off-notch →
      // pixel-identical to before on desktop / non-notched. Shared by the master
      // shell + every DashboardLayout hub. Pinned by Header.safeAreaTop.pin.test.ts.
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className={cn(
        'flex items-center justify-between px-3',
        quietMode ? 'h-10' : 'h-12',
        mobileNavHost && 'max-sm:h-14 max-sm:gap-1 max-sm:px-1.5',
      )}>
      {/* Left: Back + Mobile menu + Job Context / Breadcrumbs. With inline
          tabs, this group stops flex-growing so the tabs get the runway. */}
      <div className={cn(
        'flex items-center gap-2 min-w-0',
        tabsSlot ? 'flex-none max-w-[45%]' : 'flex-1',
        phoneHidden,
      )}>
        {/* Global "back to where you came from" affordance (operator
            2026-06-29). Self-tracks in-app history depth and hides itself on
            top-level / fresh screens with nowhere to go. Always mounted so the
            depth tracker keeps running even when the button is hidden. */}
        <HeaderBackButton breadcrumbs={breadcrumbs} />

        {/* BUG-2026-05-23-MOBILE-HAMBURGER-DUPLICATES-DOCK — on
         *  `<lg` the master MobileDock is the canonical primary
         *  navigation (Dashboard / Active Jobs / Reports / Emails /
         *  Kanban / Calendar + every cross-product hub from
         *  HUB_LINKS + Settings, all in a single thumb-reach
         *  horizontal strip — see src/components/MobileDock/index
         *  .tsx). The hamburger drawer used to be the ONLY way to
         *  see the hub list on mobile, so it shipped `lg:hidden`
         *  (visible on `<lg`) to compensate. Now that the dock
         *  surfaces every link the drawer did, this button is
         *  duplicate chrome and a UX paradox (operator 2026-05-23:
         *  "why do you have the hamburger menu if the mobile view
         *  already has the primary menu"). It is now hidden at
         *  every viewport; the `onOpenMobileSidebar` prop still
         *  flows in case a future surface wants to call it
         *  programmatically (e.g. the avatar dropdown's
         *  "Show side panel" affordance), but no visible chrome
         *  triggers it. On lg+ the desktop Sidebar takes over,
         *  same as before. One affordance per surface: dock owns
         *  mobile, sidebar owns desktop. */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault(); e.stopPropagation()
            const now = Date.now()
            if (now - lastMenuOpenRef.current < 300) return
            lastMenuOpenRef.current = now
            onOpenMobileSidebar?.('click')
          }}
          onMouseEnter={() => onOpenMobileSidebar?.('hover')}
          onTouchStart={() => { lastMenuOpenRef.current = Date.now() }}
          onTouchEnd={(e) => { e.preventDefault(); onOpenMobileSidebar?.('click') }}
          className="hidden p-1.5 -ml-1 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted cursor-pointer"
          aria-label="Open navigation menu"
          data-mobile-hamburger-disabled="true"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Job Context Pill */}
        {jobContext && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-accent border border-border rounded-md text-xs">
            <span className="font-medium text-accent-foreground">{jobContext.jobNumber}</span>
            <span className="text-accent-foreground/80 truncate max-w-48 hidden sm:block">{jobContext.jobName}</span>
            <button onClick={onClearJobContext} className="p-0.5 text-blue-400 hover:text-blue-600">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Breadcrumbs (last-3 capped when tabs share the line) */}
        {!jobContext && visibleBreadcrumbs && visibleBreadcrumbs.length > 0 && (
          <nav className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground min-w-0">
            {crumbsCapped && (
              <span
                className="text-muted-foreground/60 select-none"
                title={breadcrumbs!.map((b) => b.label).join(' › ')}
                data-quotech-crumb-ellipsis=""
              >
                …
              </span>
            )}
            {visibleBreadcrumbs.map((item, index) => (
              <div key={index} className="flex items-center gap-1 min-w-0">
                {(index > 0 || crumbsCapped) && <ChevronRight className="w-3 h-3 shrink-0 text-muted-foreground/60" />}
                {item.href?.startsWith('http') ? (
                  <a href={item.href} className="hover:text-foreground truncate">{item.label}</a>
                ) : item.href ? (
                  <Link to={item.href} className="hover:text-foreground truncate">{item.label}</Link>
                ) : (
                  <span className="text-foreground font-medium truncate">{item.label}</span>
                )}
              </div>
            ))}
          </nav>
        )}

        {/* Mobile: current-page title. The breadcrumb trail above is
            `hidden sm:flex`, so on a phone the left half of the header
            is an empty void (operator: "Diary should have been on top
            nav"). Surface the LAST crumb — the screen you're on — so
            the operator always knows where they are and the chrome
            reads as intentional. Skip when it's only "Home" (length 1)
            to avoid a redundant label on the root. Mirrors the tenant
            shells' mobilePageTitle (apps/newmark, apps/sam, apps/steel)
            and iOS / Linear mobile header patterns. */}
        {!jobContext && breadcrumbs && breadcrumbs.length > 1 && (
          <span
            data-quotech-mobile-page-title=""
            className="sm:hidden truncate text-sm font-semibold text-foreground"
            title={breadcrumbs[breadcrumbs.length - 1]?.label}
          >
            {breadcrumbs[breadcrumbs.length - 1]?.label}
          </span>
        )}
        <span
          id={HEADER_MOBILE_TITLE_SLOT_ID}
          data-quotech-header-title-slot=""
          className="flex shrink-0 items-center empty:hidden md:hidden"
        />
      </div>

      {mobileNavHost && (
        <div
          id={HEADER_MOBILE_NAV_SLOT_ID}
          data-quotech-header-nav-slot=""
          className="flex min-w-0 flex-1 items-stretch self-stretch py-1 sm:hidden"
        />
      )}

      {/* FEAT-2026-07-03-HEADER-INLINE-TABS — open-page tabs share the header
          line (the freed strip row gives the content that height back). The
          TabBar inside is `hidden md:flex`, so on phones this collapses and
          the mobile page-title/actions layout is untouched. */}
      {tabsSlot && (
        <div
          className="hidden md:flex flex-1 min-w-0 items-center self-stretch px-2"
          data-quotech-header-tabs=""
        >
          {tabsSlot}
        </div>
      )}

      {/* Center: Search (desktop). Gated `lg:flex` (was `sm:flex`): on tablet
          widths (sm–lg) the full pill crowded the breadcrumb trail — operator
          2026-07-14 "search bar overlaps the breadcrumbs on tablet". Below lg
          the pill collapses to a compact icon rendered in the right-actions
          cluster beside the What's New gift (see below), keeping breadcrumbs
          fully visible. Desktop (lg+) is unchanged. */}
      {showSearch && (
        <button
          onClick={onOpenSearch}
          className="hidden lg:flex items-center gap-2 px-3 py-1.5 text-xs text-muted-foreground bg-muted rounded-md border border-border hover:bg-accent transition-colors mx-4"
        >
          <Search className="w-3.5 h-3.5" />
          <span className="w-32">Search...</span>
          <kbd
            className="flex items-center gap-0.5 px-1 py-0.5 bg-card border border-border rounded text-[10px] font-mono"
            title={formatModFShortcut()}
          >
            {isMac() ? (
              <>
                <Command className="w-2.5 h-2.5" />F
              </>
            ) : (
              'Ctrl+F'
            )}
          </kbd>
        </button>
      )}

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        {/* Quick Add */}
        {showQuickAdd && quickAddItems.length > 0 && (
          <div ref={quickAddRef} className="relative">
            <button
              onClick={() => setQuickAddOpen(!quickAddOpen)}
              className={cn('flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md text-white', accentBgClass)}
            >
              Create
            </button>
            {quickAddOpen && (
              <div className="absolute top-full right-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg z-50 py-1">
                {quickAddItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { item.action(); setQuickAddOpen(false) }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-popover-foreground hover:bg-muted"
                  >
                    <item.icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Compact search (tablet + mobile, `<lg`). Sits beside the What's New
            gift (headerRightActionsSlot) so tablet users keep search access
            without the full center pill overlapping the breadcrumbs. Opens the
            SAME GlobalSearch overlay as the desktop pill (onOpenSearch) — no
            separate search implementation. */}
        {showSearch && (
          <button
            onClick={onOpenSearch}
            className={cn('lg:hidden p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted', phoneHidden)}
            aria-label="Search"
          >
            <Search className="w-4 h-4" />
          </button>
        )}

        {mobileNavHost ? (
          <span className="flex items-center max-sm:hidden">{headerRightActionsSlot}</span>
        ) : (
          headerRightActionsSlot
        )}

        {/* Mobile-only relocation of the UniversalDock's three
            built-in panel triggers (Report / Teams / AI Chat). On
            phones the floating bottom-right dock overlapped reading
            panes (email, kanban card detail); this strip occupies
            the empty header runway instead. Self-gates to narrow
            via ``flex lg:hidden``. UniversalDock listens for the
            click event and renders the panel as a slide-up overlay;
            at lg and above the floating bar is the canonical surface
            again. Operator complaint 2026-05-24. Suppressed when the host
            mounts its own dock (hideMobileDockTriggers) so it isn't three
            inert buttons firing events nothing listens for. */}
        {!hideMobileDockTriggers && !mobileNavHost && <MobileDockTriggerStrip />}

        {showOrgSwitcher && (() => {
          const switcher = (
            <OrgSwitcher
              organisations={organisations}
              currentOrganisationId={currentOrganisationId}
              onSelectOrganisation={onSelectOrganisation}
              onOrgClick={onOrgClick}
              user={user}
            />
          )
          return mobileNavHost && !keepOrgOnPhone ? <div className="flex max-sm:hidden">{switcher}</div> : switcher
        })()}

        {onToggleTheme && !themeInUserMenu && (
          <button
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            className={cn('p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted', phoneHidden)}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        )}

        {showNotifications && (
          <button
            onClick={onNotificationsClick}
            className="relative p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
            aria-label="Open notifications"
          >
            <Bell className="w-4 h-4" />
            {notificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 flex items-center justify-center px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                {formatCountBadge(notificationCount)}
              </span>
            )}
          </button>
        )}

        {user && (
          <UserMenu
            user={user}
            onLogout={onLogout}
            onUseProfile={onUseProfile}
            onOpenSettings={onOpenSettings}
            theme={theme}
            onToggleTheme={themeInUserMenu ? onToggleTheme : undefined}
            phoneExtras={
              mobileNavHost
                ? {
                    onOpenSearch: showSearch ? onOpenSearch : undefined,
                    theme,
                    onToggleTheme: themeInUserMenu ? undefined : onToggleTheme,
                  }
                : undefined
            }
          />
        )}
      </div>
      </div>
    </header>
  )
}

export default Header
